import React, { useRef, useEffect, useState } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import PropTypes from 'prop-types';

const libraries = ['places'];

const containerStyle = {
  width: '100%',
  height: '400px',
};

function ShowMap({ center, pickupLocation, dropoffLocation, onLocationSelect }) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyCn_HSm2EGS10vXvjJl4LAuA62uLmHCds0",
    libraries,
  });

  const [defaultCenter, setDefaultCenter] = useState(null);
  const mapRef = useRef(null);
  const directionsRendererRef = useRef(null);

  // On mount, get user location if no center provided
  useEffect(() => {
    if (!center) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
            (position) => {
              setDefaultCenter({
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              });
            },
            (error) => {
              console.warn("Geolocation permission denied or unavailable", error);
              // fallback default location (optional)
              setDefaultCenter({ lat: -26.2041, lng: 28.0473 }); // Johannesburg coords for example
            }
        );
      } else {
        // Browser doesn't support Geolocation
        setDefaultCenter({ lat: -26.2041, lng: 28.0473 }); // fallback location
      }
    }
  }, [center]);

  useEffect(() => {
    if (!isLoaded || !pickupLocation || !dropoffLocation) return;

    if (directionsRendererRef.current) {
      directionsRendererRef.current.setMap(null);
    }

    const directionsService = new window.google.maps.DirectionsService();
    directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
      suppressMarkers: false,
      polylineOptions: {
        strokeColor: 'yellow',
        strokeWeight: 5,
      },
    });

    if (mapRef.current) {
      directionsRendererRef.current.setMap(mapRef.current);
    }

    directionsService.route(
        {
          origin: pickupLocation,
          destination: dropoffLocation,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === 'OK' && directionsRendererRef.current) {
            directionsRendererRef.current.setDirections(result);
          } else {
            console.error('Directions request failed:', status);
          }
        }
    );

    return () => {
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setMap(null);
      }
    };
  }, [isLoaded, pickupLocation, dropoffLocation]);

  // Pick which center to use:
  const mapCenter = center || defaultCenter;

  if (loadError) return <div>Error loading map</div>;
  if (!isLoaded || !mapCenter) return <div>Loading map...</div>;

  const mapKey = `${mapCenter.lat}-${mapCenter.lng}`;

  const mapOptions = {
    fullscreenControl: false,
    streetViewControl: false,
    mapTypeControl: false,
    zoomControl: false,
  };

  const handleMapClick = async (event) => {
    if (!window.google) return;
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    try {
      const geocoder = new window.google.maps.Geocoder();
      const response = await new Promise((resolve, reject) => {
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === 'OK') resolve(results);
          else reject(status);
        });
      });

      let address = '';
      if (response && response[0]) {
        address = response[0].formatted_address;

        if (address.split(',').length <= 2) {
          const detailedResponse = await new Promise((resolve, reject) => {
            geocoder.geocode(
                { location: { lat, lng }, region: 'za', language: 'en' },
                (results, status) => {
                  if (status === 'OK') resolve(results);
                  else reject(status);
                }
            );
          });
          if (detailedResponse && detailedResponse[0]) {
            address = detailedResponse[0].formatted_address;
          }
        }

        onLocationSelect({ lat, lng }, address);
      } else {
        onLocationSelect({ lat, lng }, `${lat.toFixed(6)}, ${lng.toFixed(6)}`);
      }
    } catch (error) {
      console.error('Geocode error:', error);
      onLocationSelect({ lat, lng }, `${lat.toFixed(6)}, ${lng.toFixed(6)}`);
    }
  };


  return (
      <GoogleMap
          key={mapKey}
          mapContainerStyle={containerStyle}
          center={mapCenter}
          zoom={16}
          options={mapOptions}
          onClick={handleMapClick}
          onLoad={(map) => {
            mapRef.current = map;
          }}
      />
  );
}

ShowMap.propTypes = {
  center: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }),
  pickupLocation: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }),
  dropoffLocation: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }),
  onLocationSelect: PropTypes.func.isRequired,
};

ShowMap.defaultProps = {
  center: null,
  pickupLocation: null,
  dropoffLocation: null,
};

export default ShowMap;
